import { useCallback, useEffect, useRef, useState } from "react";
import { BACKEND_BASE_URL } from "../config/env";
import { getTrackingOfferwallBannerConfig } from "../config/bannerConfig";
import { getTrackingClientSession } from "../utils/clientSession";
import { getTrackingFingerprintHeaders } from "../utils/trackingFingerprint";
import AdBanner from "./AdBanner";
import "./tracking-offerwall.css";

const OFFERWALL_WAIT_SECONDS = 8;

export default function useTrackingOfferwall() {
  const [offerwall, setOfferwall] = useState(null);
  const resolverRef = useRef(null);

  const closeOfferwall = useCallback(() => {
    setOfferwall(null);
    if (resolverRef.current) {
      resolverRef.current(true);
      resolverRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!offerwall || offerwall.remaining <= 0) {
      return undefined;
    }

    const timer = window.setTimeout(() => {
      setOfferwall((current) => {
        if (!current) {
          return current;
        }
        return {
          ...current,
          remaining: Math.max(0, current.remaining - 1),
        };
      });
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [offerwall]);

  useEffect(() => {
    if (!offerwall || offerwall.remaining > 0) {
      return undefined;
    }

    const timer = window.setTimeout(closeOfferwall, 350);
    return () => window.clearTimeout(timer);
  }, [closeOfferwall, offerwall]);

  const requestOfferwall = async () => {
    if (!BACKEND_BASE_URL) {
      return true;
    }

    try {
      const params = new URLSearchParams();
      const clientSession = await getTrackingClientSession();
      const query = params.toString();
      const fingerprintHeaders = await getTrackingFingerprintHeaders();
      const requestHeaders = { ...fingerprintHeaders };
      if (clientSession) {
        requestHeaders["X-Client-Session"] = clientSession;
      }
      const res = await fetch(`${BACKEND_BASE_URL}/tracking/offerwall${query ? `?${query}` : ""}`, {
        method: "POST",
        headers: requestHeaders,
      });
      if (!res.ok) {
        return true;
      }

      const payload = await res.json();
      if (!payload?.required) {
        return true;
      }

      return new Promise((resolve) => {
        resolverRef.current = resolve;
        setOfferwall({
          count: Number(payload.count) || 0,
          interval: Number(payload.interval) || 5,
          remaining: OFFERWALL_WAIT_SECONDS,
        });
      });
    } catch {
      return true;
    }
  };

  const offerwallElement = offerwall ? (
    <div className="tracking-offerwall" role="dialog" aria-modal="true">
      <div className="tracking-offerwall-panel">
        <div className="tracking-offerwall-header">
          <div>
            <span className="tracking-offerwall-label">Iklan</span>
            <h2>Mohon tunggu sebentar</h2>
          </div>
          <button
            type="button"
            className="tracking-offerwall-close"
            onClick={closeOfferwall}
            disabled={offerwall.remaining > 0}
            aria-label="Tutup iklan"
            title={offerwall.remaining > 0 ? `Bisa ditutup dalam ${offerwall.remaining} detik` : "Tutup iklan"}
          >
            x
          </button>
        </div>
        <AdBanner {...getTrackingOfferwallBannerConfig()} />
        <p className="tracking-offerwall-copy">
          Setelah beberapa kali cek resi, kami menampilkan iklan singkat agar layanan tetap gratis.
        </p>
        <button
          type="button"
          className="button-primary tracking-offerwall-button"
          onClick={closeOfferwall}
          disabled={offerwall.remaining > 0}
        >
          {offerwall.remaining > 0
            ? `Lanjut dalam ${offerwall.remaining} detik`
            : "Lanjut cek resi"}
        </button>
      </div>
    </div>
  ) : null;

  return { requestOfferwall, offerwallElement };
}
